import React, { memo, ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    AvatarComProps,
    CustomModal,
    MainPageLayout,
    MainPageLayoutItem,
    MainPageLayoutTreeItem,
    SetAccountModal,
    SOUND_MAP,
    SpecialEffectIconUsed,
    SpecialEffectRenderMap,
    UpdateRecord,
    WindowsSystemBtnItem,
} from "remo-components";
import { routeConfig, RouteNameType } from "../../route-config";
import { SideMenuTypes } from "../../../../remo-types/src/constants";
import {
    useChannelStore,
    useChannelRoomStore,
    useGlobalStore,
    clearStore,
    UserInfo,
    channelStore,
    useChatStore,
    SpecialEffectTo,
    PreferencesStoreState,
    createPreferencesStore,
    InterActionMessageType,
} from "@obsbot/remo-stores";
import { usePushHistory } from "../../utils/routes";
import {
    logout,
    quitAllChannel,
    refreshToken,
    updateUserInfo,
    uploadFile,
} from "@obsbot/remo-server-api";
import { invoke, listenEvent, removeEvent, send, sendToOverlay } from "../../utils/ipc";
import {
    CustomType,
    ICustomPayload,
    ICustomPayloadDescription,
    InterActionMessageDetail,
    SDKMsgType,
} from "@obsbot/remo-services";
import { eventBus } from "@obsbot/remo-utils";
import { RemoI18nLanguagesEnum, Trans, useLanguage, useTranslate } from "@obsbot/remo-i18n";
import { UpdateVersionInfo } from "remo-types/src/ipc";

export interface MainPageLayoutContainerProps {
    subMenu?: MainPageLayoutTreeItem[];
    activeKeys?: string[];
    children?: ReactNode;
    onClickSystemBtn?: (winSystemBtn: WindowsSystemBtnItem) => void;
}

const MainPageLayoutContainerCom: React.FC<MainPageLayoutContainerProps> = ({
    subMenu,
    activeKeys,
    children,
    onClickSystemBtn,
}): ReactElement => {
    const location = useLocation();
    const globalStore = useGlobalStore();
    activeKeys ??= [location.pathname];
    const [isLogin, setIsLogin] = useState(activeKeys.includes("/login"));
    const [isInvite, setIsInvite] = useState(activeKeys.includes("/channel/invite"));
    const isShowOverlay = ![
        routeConfig[RouteNameType.AccountSettingPage].path,
        routeConfig[RouteNameType.RTCSettingPage].path,
        routeConfig[RouteNameType.DisplaySettingPage].path,
        routeConfig[RouteNameType.GameOverlaySettingPage].path,
        routeConfig[RouteNameType.HotKeySettingPage].path,
    ].includes(location.pathname as RouteNameType);

    // 是否下载完成安装包
    const [isShowVersionInfo, setIsShowVersionInfo] = useState(false);
    const [versionInfo, setVersionInfo] = useState<UpdateVersionInfo | null>(null);

    const [preferenceStore, setPreferenceStore] = useState<PreferencesStoreState | null>(null);

    useEffect(() => {
        if (globalStore.userInfo?.user_id) {
            const store = createPreferencesStore(globalStore.userInfo.user_id);
            // 立即设置初始状态
            setPreferenceStore(store.getState());
            // 订阅后续更新
            const unsubscribe = store.subscribe(state => setPreferenceStore(state));
            return () => {
                unsubscribe();
            };
        } else {
            setPreferenceStore(null);
            return () => {
                //
            };
        }
    }, [globalStore.userInfo]);

    // 防止preferenceStore不是最新的
    useEffect(() => {
        if (preferenceStore && preferenceStore.showAnimations === undefined) {
            preferenceStore.updateShowAnimations(["BombIcon", "WelcomeIcon", "CryIcon"]);
        }
    }, [preferenceStore]);

    const navigate = useNavigate();
    const pushHistory = usePushHistory();
    const t = useTranslate();
    const lang = useLanguage();
    const onMenuItemClick = (mainPageLayoutItem: MainPageLayoutItem): void => {
        if (mainPageLayoutItem.route.startsWith("/")) {
            navigate(mainPageLayoutItem.route);
        }
    };

    const [isShowSetDialog, setIsShowSetDialog] = useState(false);
    const [isShowExitWarning, setIsShowExitWarning] = useState(false);

    useEffect(() => {
        if (location.pathname.includes("/login")) {
            !isLogin && setIsLogin(true);
        } else {
            isLogin && setIsLogin(false);
        }
        if (location.pathname.includes("/channel/invite")) {
            !isInvite && setIsInvite(true);
        } else {
            isInvite && setIsInvite(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    useEffect(() => {
        if (isLogin) {
            const validToken = async (): Promise<void> => {
                const refresh_token = localStorage.getItem("refresh_token");
                const globalStore = useGlobalStore.getState();
                if (!refresh_token || !globalStore.userInfo) {
                    return;
                }
                try {
                    const { token } = await refreshToken({
                        refresh_token,
                        client_type: (window as any).isElectron ? "game_client" : "web",
                    });
                    globalStore.updateUserToken(token);
                    localStorage.setItem("token", token);
                    if (token) {
                        const { data } = await globalStore.getSdkID();
                        globalStore.getUserInfo(globalStore.userInfo?.notSet || false);
                        globalStore.updateRTCInfo({
                            userSig: data.userSig,
                            uid: globalStore.userInfo!.user_id,
                        });
                        globalStore.updateServerRegionConfig({
                            tencent: { appId: data.sdkAppId },
                        });
                        globalStore.updateOnlineStatus("online");
                        pushHistory(RouteNameType.OnlineFriendsPage);
                    }
                } catch (error) {
                    return;
                }
            };
            validToken();
        }
    }, [isLogin, pushHistory]);

    useEffect(() => {
        if (globalStore.userInfo && globalStore.userInfo.notSet) {
            setIsShowSetDialog(true);
        }
    }, [globalStore.userInfo]);

    const { rtm } = useChatStore();

    // 如果RTM无法得到用户的名称和头像则提示
    useEffect(() => {
        const checkProfile = async (): Promise<void> => {
            const userId = useGlobalStore.getState().userInfo?.user_id;
            if (userId) {
                const profile = (await rtm!.getUserProfile([userId]))[0];
                (!profile.nick || !profile.avatar) && setIsShowSetDialog(true);
            }
        };
        if (rtm && !isLogin) {
            checkProfile();
        }
    }, [rtm, isLogin]);

    // 监听用户sdk被多实例登出
    useEffect(() => {
        if (rtm) {
            rtm.events.on("kicked-out", () => {
                console.log("用户多实例登录被踢出");
                eventBus.emit("show-message", {
                    message: t("user_kicked_out"),
                    type: "error",
                });
                // 退出登录
                navigate(routeConfig[RouteNameType.LoginPage].path);
                // handleQuitAccount();
            });
        }
        return () => {
            if (rtm) {
                rtm.events.off("kicked-out", () => {
                    console.log("取消监听");
                });
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rtm]);

    useEffect(() => {
        (window as any).electron?.ipcRenderer.on("before-close", async () => {
            await clearStore();
            await logout();
            (window as any).electron?.ipcRenderer.send("set-win-status", {
                windowStatus: "destroy",
            });
        });

        return () => (window as any).electron?.ipcRenderer.removeAllListeners("before-close");
    }, [globalStore]);

    useEffect(() => {
        const checkAppState = async (): Promise<void> => {
            const appState = await invoke("get-app-state");
            if (appState === "gamechat") {
                setIsShowExitWarning(true);
            }
        };
        checkAppState();
    }, []);

    const onSideMenuClick = (sideMenuActiveKey: SideMenuTypes): void => {
        if (sideMenuActiveKey === SideMenuTypes.USER) {
            navigate(routeConfig[RouteNameType.AddFriendsPage].path);
        } else if (sideMenuActiveKey === SideMenuTypes.CHANNEL) {
            // 处理已经在房间的情况
            const { activeChannel } = useChannelStore.getState();
            const { isJoinedRTC } = useChannelRoomStore.getState();
            if (activeChannel && isJoinedRTC) {
                console.log("[WEB] 重新进入频道", activeChannel);
                if (!activeChannel) {
                    return;
                }
                pushHistory(RouteNameType.ChannelRoomPage, {
                    roomUUID: activeChannel?.channelUuid,
                });
            } else {
                navigate(routeConfig[RouteNameType.ChannelPage].path);
            }
        } else {
            navigate(routeConfig[RouteNameType.AccountSettingPage].path);
        }
    };

    const onClickChatSideBar = (key: string, userInfo?: AvatarComProps): void => {
        const curKey = key as RouteNameType;
        if (userInfo) {
            pushHistory(curKey, {
                id: userInfo.userID,
                name: userInfo.userName?.toString().trim() || userInfo.userID,
            });
        } else {
            navigate(routeConfig[curKey].path);
        }
    };

    // 跳转到个人设置
    const handlePersonalSetting = (): void => {
        navigate(routeConfig[RouteNameType.AccountSettingPage].path);
    };

    // 退出账号
    const handleQuitAccount = async (): Promise<void> => {
        // 跳转到登录页
        await clearStore();
        logout();
        // 退出账号，还有一些清理工作
        localStorage.removeItem("token");
        localStorage.removeItem("refresh_token");
        navigate(routeConfig[RouteNameType.LoginPage].path);
    };

    const handleOverlay = (): void => {
        // TODO: 显示/隐藏聊天窗口
        pushHistory(RouteNameType.GameOverlaySettingPage);
    };

    const onHandleLeave = (): void => {
        // 挂断
        channelStore.updateUserState({
            mic: false,
            camera: false,
            isListen: false,
        });
    };

    const handleSetAccount = async (username: string, logo: string): Promise<void> => {
        // 调用更新用户信息的接口
        const curUserInfo = {
            logo: logo || globalStore.userInfo?.logo,
            nickname: username,
        };
        // 调用后端接口
        const res = await updateUserInfo(curUserInfo);
        if (res.user_id) {
            // 更新store用户信息
            globalStore.updateUserInfo({
                ...globalStore.userInfo,
                ...curUserInfo,
                notSet: false,
            } as UserInfo);
            setIsShowSetDialog(false);
            return;
        }
    };

    const handleUpload = async (file: any): Promise<any> => {
        const res = await uploadFile({
            type: "normal",
            md5_flag: true,
            files: file,
        });
        return res?.files || [];
    };

    useEffect(() => {
        const handleRemoteUserLeave = (uid: string): void => {
            sendToOverlay("remote-user-leave", uid);
        };
        eventBus.on("user-leave-channel", handleRemoteUserLeave);
        return () => {
            eventBus.off("user-leave-channel", handleRemoteUserLeave);
        };
    }, []);

    // 处理特效交互的逻辑
    useEffect(() => {
        listenEvent("send-interaction-message", async ({ icon, to, toName, actionID, toType }) => {
            const channelroomStore = useChannelRoomStore.getState();
            let messagePayload: ICustomPayload;
            if (toType === SpecialEffectTo.All) {
                messagePayload = {
                    data: icon,
                    description: ICustomPayloadDescription.ChannelInteraction,
                    extension: toType,
                };
            } else {
                messagePayload = {
                    data: icon,
                    description: ICustomPayloadDescription.ChannelInteraction,
                    extension: JSON.stringify({
                        toType,
                        userID: to,
                        userName: toName,
                    }),
                };
            }
            const result = await rtm?.sendRoomMessage(CustomType.Custom, messagePayload);
            if (result === "") {
                return;
            }
            eventBus.emit("send-new-msg", {
                userID: globalStore.userInfo?.user_id || "",
                conversationID: "GROUP" + channelroomStore.roomUUID,
            });
            channelroomStore.updateSpecialList([
                ...channelroomStore.channelSpecialList,
                {
                    icon: SpecialEffectRenderMap[icon as SpecialEffectIconUsed],
                    toType,
                    to,
                    toName,
                    sender: channelroomStore.userUUID,
                    actionID,
                    soundEffect: icon,
                },
            ]);
        });
        listenEvent("update-special-list", data => {
            const channelroomStore = useChannelRoomStore.getState();
            channelroomStore.updateSpecialList([...data]);
        });
        listenEvent("special-effect-done", actionID => {
            const channelRoomStore = useChannelRoomStore.getState();
            channelRoomStore.updateSpecialList(
                channelRoomStore.channelSpecialList.filter(item => item.actionID !== actionID),
            );
        });
        return () => {
            removeEvent("update-special-list");
            removeEvent("send-interaction-message");
            removeEvent("special-effect-done");
        };
    }, [globalStore.userInfo?.user_id, rtm]);

    useEffect(() => {
        const handleInterActionMessage = ({
            message,
            to,
            from,
            actionID,
        }: InterActionMessageDetail): void => {
            const channelroomStore = useChannelRoomStore.getState();
            const icon = message.data as SpecialEffectIconUsed;
            const toType = message.extension as SpecialEffectTo;
            let toSomeone = to;
            if (toType !== SpecialEffectTo.All) {
                toSomeone = JSON.parse(toType).userName;
            }
            if (SpecialEffectRenderMap[icon]) {
                channelroomStore.updateSpecialList([
                    ...channelroomStore.channelSpecialList,
                    {
                        icon: SpecialEffectRenderMap[icon],
                        toType,
                        to: toSomeone,
                        sender: from,
                        actionID,
                        soundEffect: icon,
                    },
                ]);
            }
        };
        /**
         * 限制只有允许的类型以及非自动的消息可以造成任务栏闪烁
         *  - 允许的类型 {@link SDKMsgType}
         *  - 不是自动消息, 由于目前进入频道前依旧会发送自定义消息, 所以也将其排除
         *  - 其余排除类型需同样按照以下方式声明并提前返回
         */
        const handleReceiveMessage = (message: any): void => {
            const allowTypes = Object.values(SDKMsgType);
            const isAllowType = allowTypes.includes(message.type);
            const isAutoMessage =
                message.type === SDKMsgType.MSG_CUSTOM &&
                message.payload.ICustomPayloadDescription ===
                    ICustomPayloadDescription.ChannelCommand;
            if (isAutoMessage || !isAllowType) {
                return;
            }
            send("receive-message");
        };
        if (rtm) {
            rtm.events.on("message-received", handleReceiveMessage);
            rtm.events.on("get-interaction-message", handleInterActionMessage);
        }
        return () => {
            rtm?.events.off("message-received", handleReceiveMessage);
            rtm?.events.off("get-interaction-message", handleInterActionMessage);
        };
    }, [rtm]);

    const {
        channelSpecialList,
        channelAudioList,
        appendAudioList,
        removeAudioList,
        updateAudioList,
    } = useChannelRoomStore();
    // 用于标记已经入列/已处理的 actionID，避免重复入队
    const processedActionIdsRef = useRef<Set<string>>(new Set());
    // 每个 sender 对应一个队列
    const queuesRef = useRef<
        Record<string, Array<Pick<InterActionMessageType, "soundEffect" | "actionID" | "sender">>>
    >({});
    // 表示某个 sender 是否正在播放
    const playingRef = useRef<Record<string, boolean>>({});
    // 记录所有正在播放的 Audio 实例，组件卸载时需要清理
    const activeAudiosRef = useRef<HTMLAudioElement[]>([]);
    const audioRefs = useRef<Record<string, HTMLAudioElement>>({});

    // helper：获得队列 key（基于 sender，缺失时归为 __global）
    const getQueueKey = (item: any): string => (item?.sender ? String(item.sender) : "__global");

    // 播放某个 sender 的下一个音频
    const playNextForSender = (senderKey: string): void => {
        const queue = queuesRef.current[senderKey] || [];
        const next = queue.shift();
        if (!next) {
            playingRef.current[senderKey] = false;
            return;
        }
        playingRef.current[senderKey] = true;

        const soundPathKey = next.soundEffect;
        const src = SOUND_MAP[soundPathKey as SpecialEffectIconUsed];
        if (!src) {
            // 继续播放下一条
            playNextForSender(senderKey);
            return;
        }
        // 为支持并发播放，不直接使用预加载的 audio 元素去 play，而是基于 src 新建实例
        const audioEl = new Audio(src);
        audioEl.volume = 0.1;
        activeAudiosRef.current.push(audioEl);
        const cleanupAudio = (): void => {
            // 从 active 列表中移除
            const idx = activeAudiosRef.current.indexOf(audioEl);
            if (idx >= 0) {
                activeAudiosRef.current.splice(idx, 1);
            }
            audioEl.pause();
            audioEl.src = "";
        };
        audioEl.addEventListener("ended", () => {
            cleanupAudio();
            // 继续下一个
            playNextForSender(senderKey);
        });
        audioEl.addEventListener("error", () => {
            cleanupAudio();
            playNextForSender(senderKey);
        });
        // 尝试播放
        audioEl.play().catch(() => {
            // 若播放失败则清理并继续下一个
            cleanupAudio();
            playNextForSender(senderKey);
        });
    };

    useEffect(() => {
        // 预加载所有音频源（保留用于快速创建播放实例的参考）
        const allSoundPaths = new Set<string>();
        Object.values(SOUND_MAP).forEach(path => allSoundPaths.add(path));
        allSoundPaths.forEach(src => {
            if (!audioRefs.current[src]) {
                const audio = new Audio(src);
                audio.load();
                audio.volume = 0.1;
                audioRefs.current[src] = audio;
            }
        });

        return () => {
            // 清理所有正在播放的 audio 实例
            activeAudiosRef.current.forEach(a => {
                try {
                    a.pause();
                    a.src = "";
                } catch {
                    /* ignore */
                }
            });
            activeAudiosRef.current = [];
            // 清理预加载引用
            Object.values(audioRefs.current).forEach(audio => {
                try {
                    audio.pause();
                    audio.src = "";
                } catch {
                    /* ignore */
                }
            });
            audioRefs.current = {};
            queuesRef.current = {};
            processedActionIdsRef.current.clear();
            playingRef.current = {};
        };
    }, []);

    useEffect(() => {
        // 更新特效列表到 overlay（不变）
        console.log("[WEB] DEBUG 查看当前特效列表", channelSpecialList);
        sendToOverlay("update-special-list", channelSpecialList);
    }, [channelSpecialList]);

    useEffect(() => {
        // 将新增的 channelAudioList 项入各自 sender 队列并启动播放（每个 sender 的队列互不影响）
        const currentActionSet = new Set<string>(channelAudioList.map(a => a.actionID));

        // 移除已经从后端删除的 actionID（从 processed 集合与队列中移除；如果正在播放则允许完成）
        for (const processed of Array.from(processedActionIdsRef.current)) {
            if (!currentActionSet.has(processed)) {
                processedActionIdsRef.current.delete(processed);
                // 从所有队列中移除该 actionID（若尚未播放）
                for (const key of Object.keys(queuesRef.current)) {
                    queuesRef.current[key] = queuesRef.current[key].filter(
                        i => i.actionID !== processed,
                    );
                }
            }
        }

        // 将新出现的 actionID 加入对应 sender 队列
        channelAudioList.forEach(item => {
            if (!processedActionIdsRef.current.has(item.actionID)) {
                processedActionIdsRef.current.add(item.actionID);
                const key = getQueueKey(item);
                if (!queuesRef.current[key]) {
                    queuesRef.current[key] = [];
                }
                queuesRef.current[key].push(item as any);
            }
        });

        // 启动未在播放状态的 sender 队列
        Object.keys(queuesRef.current).forEach(key => {
            if (!playingRef.current[key] && queuesRef.current[key].length > 0) {
                playNextForSender(key);
            }
        });
    }, [channelAudioList]);

    // 处理音频特效交互
    useEffect(() => {
        listenEvent("audio-effect-action", ({ action, audio }) => {
            switch (action) {
                case "append": {
                    appendAudioList(
                        audio as Pick<InterActionMessageType, "soundEffect" | "actionID">,
                    );
                    break;
                }
                case "remove": {
                    removeAudioList(
                        audio as Pick<InterActionMessageType, "soundEffect" | "actionID">,
                    );
                    break;
                }
                case "update": {
                    updateAudioList(
                        audio as Array<Pick<InterActionMessageType, "soundEffect" | "actionID">>,
                    );
                    break;
                }
                default: {
                    break;
                }
            }
        });
    }, [appendAudioList, removeAudioList, updateAudioList]);

    const toSetting = (toVideo = false): void => {
        if (toVideo) {
            pushHistory(RouteNameType.RTCSettingPage, {}, { tab: "videoSetting" });
        } else {
            pushHistory(RouteNameType.RTCSettingPage);
        }
    };

    // 取消重连至频道
    const handleDisconnectToRoom = async (): Promise<void> => {
        await quitAllChannel();
        setIsShowExitWarning(false);
    };
    // 重连至频道
    const handleReconnectToRoom = async (): Promise<void> => {
        const reconnectRoomID = preferenceStore?.exitRoomID;
        console.log("[WEB] [LOG] 查看重连房间ID", reconnectRoomID);
        if (!reconnectRoomID) {
            return;
        }
        const channelStore = useChannelStore.getState();
        if (!channelStore.channels.has(reconnectRoomID)) {
            const result = await channelStore.queryChannelInfo(reconnectRoomID);
            if (!result) {
                eventBus.emit("show-message", {
                    message: t("channel-not-exist"),
                    type: "warning",
                });
                return;
            }
            const channelRoomStore = useChannelRoomStore.getState();
            if (reconnectRoomID === channelRoomStore.roomUUID) {
                globalStore.updateToJoinRoomUUID("");
            } else {
                globalStore.updateToJoinRoomUUID(reconnectRoomID);
            }
            const realNewChannel = useChannelStore.getState().channels.get(reconnectRoomID);
            if (realNewChannel) {
                channelStore.updateActiveChannel(realNewChannel);
            }
        }
        pushHistory(RouteNameType.ChannelRoomPage, {
            roomUUID: reconnectRoomID,
        });
        setIsShowExitWarning(false);
    };

    // 处理版本更新的事件
    useEffect(() => {
        const invokeEvent = async (): Promise<void> => {
            const info = await invoke("check-update-version");
            setVersionInfo(info);
            setIsShowVersionInfo(true);
        };
        invokeEvent();
        listenEvent("update-pack-info", packInfo => {
            if (packInfo.type === "downloading") {
                console.log("[DEBUG LOG] 查看下载的进度", packInfo.percent);
            } else {
                setVersionInfo(packInfo);
                setIsShowVersionInfo(true);
            }
        });
        return () => {
            removeEvent("update-pack-info");
        };
    }, []);

    const formatLang = useMemo(() => {
        switch (lang) {
            case RemoI18nLanguagesEnum.zhCN: {
                return "zh-CN";
            }
            case RemoI18nLanguagesEnum.zhHant: {
                return "zh-TW";
            }
            case RemoI18nLanguagesEnum.en: {
                return "en-US";
            }
            case RemoI18nLanguagesEnum.KoKR: {
                return "ko-KR";
            }
            case RemoI18nLanguagesEnum.ar: {
                return "ar-AE";
            }
            default: {
                return "en-US";
            }
        }
    }, [lang]);

    const versionDesc = useMemo(() => {
        if (versionInfo && versionInfo.versionDesc) {
            const changelog = versionInfo.versionDesc.find(info => info.lan === formatLang);
            return changelog?.desc.replaceAll("\\n", "\n").split("\n") ?? [""];
        }
        return [""];
    }, [versionInfo, formatLang]);

    const renderHeader = useMemo(() => {
        let i18n_key = "settings.software-version.";
        switch (versionInfo?.type) {
            case "new-version": {
                i18n_key += "find-new-version";
                break;
            }
            case "download-ready": {
                i18n_key += "versionReady";
                break;
            }
            case "package-broken": {
                i18n_key += "packageBroken";
                break;
            }
            case "update-failed": {
                i18n_key += "updateFailedTitle";
                break;
            }
            case "space-insufficient": {
                i18n_key += "diskInsufficient";
                break;
            }
        }
        return t(i18n_key);
    }, [versionInfo, t]);

    const renderContent = useMemo(() => {
        switch (versionInfo?.type) {
            case "new-version": {
                return (
                    <>
                        <div className="version-show">
                            {t("settings.software-version.version")} v{versionInfo?.version}
                        </div>
                        <div className="update-change-log">
                            <UpdateRecord
                                recordList={[
                                    {
                                        version: versionInfo?.version ?? "",
                                        update_time: "",
                                        content: versionDesc,
                                    },
                                ]}
                                showDot={false}
                            />
                        </div>
                    </>
                );
            }
            case "download-ready": {
                return t("settings.software-version.versionReadyTip", {
                    version: versionInfo?.version,
                });
            }
            case "package-broken": {
                return t("settings.software-version.packageBrokenTip");
            }
            case "update-failed": {
                return t("settings.software-version.updateFailed");
            }
            case "space-insufficient": {
                const spaceInfo = versionInfo.spaceInsufficient;
                return (
                    <Trans
                        components={{
                            space: <span className="remaining-space" />,
                        }}
                        i18nKey="settings.software-version.diskInsufficientTip"
                        values={{
                            disk: spaceInfo?.disk,
                            space: spaceInfo?.remainSize,
                            minSpace: spaceInfo?.minSize,
                        }}
                    />
                );
            }
        }
        return <div></div>;
    }, [versionInfo, t, versionDesc]);

    const renderFooter = useMemo(() => {
        switch (versionInfo?.type) {
            case "new-version": {
                return (
                    <div className="modal-button-group-full">
                        <div
                            className="group-modal-button-item"
                            onClick={() => {
                                invoke("update-version", {
                                    ...versionInfo,
                                    action: "download_pack",
                                });
                                setIsShowVersionInfo(false);
                            }}
                        >
                            {t("settings.software-version.downloadNow")}
                        </div>
                    </div>
                );
            }
            case "download-ready": {
                return (
                    <div className="modal-button-group-center" style={{ marginTop: 65 }}>
                        <div className="normal-button" onClick={() => setIsShowVersionInfo(false)}>
                            {t("settings.software-version.restartLater")}
                        </div>
                        <div
                            className="group-modal-button-item"
                            onClick={() => {
                                invoke("update-version", { ...versionInfo, action: "install" });
                            }}
                        >
                            {t("settings.software-version.restartNow")}
                        </div>
                    </div>
                );
            }
            case "package-broken": {
                return (
                    <div className="modal-button-group-center" style={{ marginTop: 65 }}>
                        <div
                            className="normal-button"
                            onClick={() => {
                                const a = document.createElement("a");
                                a.href = versionInfo.packUrl;
                                a.rel = "noreferrer";
                                a.target = "_blank";
                                a.onclick = function (e) {
                                    e.preventDefault();
                                };
                                setIsShowVersionInfo(false);
                            }}
                        >
                            {t("settings.software-version.manualDownload")}
                        </div>
                        <div
                            className="group-modal-button-item"
                            onClick={() => {
                                invoke("update-version", {
                                    ...versionInfo,
                                    action: "download_pack",
                                });
                                setIsShowVersionInfo(false);
                            }}
                        >
                            {t("settings.software-version.redownload")}
                        </div>
                    </div>
                );
            }
            case "update-failed": {
                return (
                    <div className="modal-button-group-center" style={{ marginTop: 65 }}>
                        <div className="normal-button" onClick={() => setIsShowVersionInfo(false)}>
                            {t("settings.software-version.manualDownload")}
                        </div>
                        <div
                            className="group-modal-button-item"
                            onClick={() => {
                                invoke("update-version", {
                                    ...versionInfo,
                                    action: "install",
                                });
                            }}
                        >
                            {t("settings.software-version.redownload")}
                        </div>
                    </div>
                );
            }
            case "space-insufficient": {
                return (
                    <div className="modal-button-group-center" style={{ marginTop: 65 }}>
                        <div
                            className="normal-button"
                            onClick={() => {
                                invoke("update-version", { ...versionInfo, action: "explorer" });
                                setIsShowVersionInfo(false);
                            }}
                        >
                            {t("settings.software-version.changePath")}
                        </div>
                        <div
                            className="group-modal-button-item"
                            onClick={() => {
                                invoke("update-version", { ...versionInfo, action: "explorer" });
                                setIsShowVersionInfo(false);
                            }}
                        >
                            {t("settings.software-version.diskCleanup")}
                        </div>
                    </div>
                );
            }
        }
        return "";
    }, [versionInfo, t]);

    return (
        <>
            {isLogin || isInvite ? (
                children
            ) : (
                <MainPageLayout
                    activeKeys={activeKeys}
                    handleOverlay={handleOverlay}
                    handlePersonalSetting={handlePersonalSetting}
                    handleQuitAccount={handleQuitAccount}
                    isShowOverlay={isShowOverlay}
                    sideMenuActiveKey={SideMenuTypes.USER}
                    subMenu={subMenu}
                    toSetting={toSetting}
                    onClick={onMenuItemClick}
                    onClickChatSideBar={onClickChatSideBar}
                    onClickSystemBtn={onClickSystemBtn}
                    onHandleLeave={onHandleLeave}
                    onSideMenuClick={onSideMenuClick}
                >
                    {children}
                    {globalStore.userInfo && (
                        <SetAccountModal
                            handleUpload={handleUpload}
                            setVisible={setIsShowSetDialog}
                            userAvatar={globalStore.userInfo.avatar}
                            username={globalStore.userInfo?.nickname}
                            visible={isShowSetDialog}
                            onConfirm={handleSetAccount}
                        />
                    )}
                    {!isLogin && (
                        <CustomModal
                            className="exitWarningModal"
                            footer={
                                <div className="modal-button-group-center">
                                    <div className="normal-button" onClick={handleDisconnectToRoom}>
                                        {t("cancel")}
                                    </div>
                                    <div
                                        className="group-modal-button-item"
                                        onClick={handleReconnectToRoom}
                                    >
                                        {t("confirm")}
                                    </div>
                                </div>
                            }
                            open={isShowExitWarning}
                            renderHeader={t("settings.account-setting.warning")}
                            setVisible={setIsShowExitWarning}
                        >
                            <div className="modal-content" style={{ marginTop: 12 }}>
                                {t("exitWarning")}
                            </div>
                        </CustomModal>
                    )}
                </MainPageLayout>
            )}
            <CustomModal
                className={`updateWarningModal updateVersion-${versionInfo?.type}`}
                closable={!versionInfo?.error}
                footer={renderFooter}
                open={!!versionInfo && isShowVersionInfo}
                renderHeader={renderHeader}
                setVisible={setIsShowVersionInfo}
                width={392}
            >
                <div className="modal-content" style={{ marginTop: 12 }}>
                    {renderContent}
                </div>
            </CustomModal>
        </>
    );
};

export const MainPageLayoutContainer = memo(MainPageLayoutContainerCom);
